<template>
  <div class="baseWeather" :class="[`size-${props.size}`]" :style="{ color: props.color }">
    <div v-if="props.mode === 'text'" class="textMode" @click="panelVisible = !panelVisible">
      <div class="city">{{ cityName }}</div>
      <div class="icon" :style="{ fontSize: `${props.iconSize / 100}rem` }">
        <i :class="`qi-${nowWeatherState.icon}`"></i>
      </div>
      <span class="text">{{ nowWeatherState.text }}</span>
      <span class="temp">{{ nowWeatherState.temp }}℃</span>
    </div>

    <div v-if="props.mode === 'card'" class="cardMode" @click="panelVisible = !panelVisible">
      <div class="icon" :style="{ fontSize: `${props.iconSize / 100}rem` }">
        <i :class="`qi-${nowWeatherState.icon}`"></i>
      </div>
      <div>
        <div class="city">{{ cityName }}</div>
        <div>
          <span class="text">{{ nowWeatherState.text }}</span>
          <span class="temp">{{ nowWeatherState.temp }}℃</span>
        </div>
      </div>
    </div>

    <div v-if="props.showPanel && panelVisible" class="panel">
      <div v-for="item in dailyWeatherState" :key="item.fxDate" class="panel-item">
        {{ item.fxDate.slice(5) }}
        <span style="min-width: 0.5rem; display: inline-block; text-align: center">{{
          item.textDay
        }}</span>
        {{ item.tempMax }}℃/{{ item.tempMin }}℃
      </div>
      <div class="panel-search">
        <div class="title">
          <a-select
            v-model:value="cityKeyword"
            show-search
            placeholder="搜索城市"
            style="width: 150px"
            size="small"
            :options="cityOptions"
            :filter-option="false"
            :not-found-content="fetching ? undefined : null"
            :getPopupContainer="(triggerNode: HTMLElement) => triggerNode.parentNode"
            @focus="handleFocus"
            @select="handleSelect"
            @search="handleSearch"
          ></a-select>
        </div>
        <div class="extra">
          <CloseOutlined class="close" @click="panelVisible = false" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type {
  NowData,
  DailyItem,
  WeatherBase,
  WeatherNowData,
  WeatherDailyData,
  CityData,
  LocationItem,
} from './types'
// import { message } from 'ant-design-vue'
import { CloseOutlined } from '@ant-design/icons-vue'
import { debounce } from 'lodash-es'

defineOptions({
  name: 'BaseWeather',
})

type Props = {
  mode?: 'text' | 'card' // 显示模式
  size?: 'small' | 'medium' | 'large' // 尺寸（文字大小）
  iconSize?: number // 图标大小，仅在card模式下生效
  color?: string // 字体颜色
  showPanel?: boolean // 是否显示天气面板
}
const props = withDefaults(defineProps<Props>(), {
  mode: 'text',
  size: 'medium',
  iconSize: 32,
  color: '#ffffff',
  showPanel: false,
})

const hfApiKey = 'ec47337c5b84428a834ad756ede56abe'
const hfApiUrl = 'https://np3jphtqn3.re.qweatherapi.com'

const cityName = ref('合肥')
const cityCode = ref('101220101')
const cityKeyword = ref(cityName.value)

// 初始化时尝试从缓存加载数据
const loadCachedWeather = () => {
  // 加载实时天气缓存
  const cachedNowData = localStorage.getItem(`weather_now_${cityCode.value}`)
  const nowUpdateTime = localStorage.getItem(`weather_now_time_${cityCode.value}`)
  if (cachedNowData && nowUpdateTime) {
    const cachedDate = new Date(nowUpdateTime)
    const today = new Date()
    if (
      cachedDate.getDate() === today.getDate() &&
      cachedDate.getMonth() === today.getMonth() &&
      cachedDate.getFullYear() === today.getFullYear()
    ) {
      nowWeatherState.value = JSON.parse(cachedNowData)
    }
  }

  // 加载每日天气缓存
  const cachedDailyData = localStorage.getItem(`weather_daily_${cityCode.value}_3d`)
  const dailyUpdateTime = localStorage.getItem(`weather_daily_time_${cityCode.value}_3d`)
  if (cachedDailyData && dailyUpdateTime) {
    const cachedDate = new Date(dailyUpdateTime)
    const today = new Date()
    if (
      cachedDate.getDate() === today.getDate() &&
      cachedDate.getMonth() === today.getMonth() &&
      cachedDate.getFullYear() === today.getFullYear()
    ) {
      dailyWeatherState.value = JSON.parse(cachedDailyData)
    }
  }
}

const nowWeatherState = ref<NowData>({
  obsTime: '', // 数据观测时间
  temp: '', // 温度，默认单位：摄氏度
  feelsLike: '', // 体感温度，默认单位：摄氏度
  icon: '', // 天气状况的图标代码，另请参考天气图标项目
  text: '', // 天气状况的文字描述，包括阴晴雨雪等天气状态的描述
  wind360: '', // 风向360角度
  windDir: '', // 风向
  windScale: '', // 风力等级
  windSpeed: '', // 风速，公里/小时
  humidity: '', // 相对湿度，百分比数值
  precip: '', // 过去1小时降水量，默认单位：毫米
  pressure: '', // 大气压强，默认单位：百帕
  vis: '', // 能见度，默认单位：公里
  cloud: '', // 云量，百分比数值。可能为空
  dew: '', // 露点温度。可能为空
})

const dailyWeatherState = ref<DailyItem[]>([])
const fxLink = ref('')

// 初始化时加载缓存数据
loadCachedWeather()

/**
 * 基础请求函数
 * @param url 请求路径
 * @param params 请求参数
 * @returns 响应数据
 */
const baseFetch = async <T extends WeatherBase = WeatherBase>(
  url: string,
  params: Record<string, string>
): Promise<T> => {
  const queryString = new URLSearchParams(params).toString()
  const fullUrl = `${hfApiUrl}${url}?${queryString}`
  return new Promise((resolve, reject) => {
    fetch(fullUrl, {
      headers: {
        'X-QW-Api-Key': hfApiKey,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((data) => resolve(data))
      .catch((error) => reject(error))
  })
}

/**
 * 获取实时天气
 * 请求路径：/v7/weather/now
 * @see https://dev.qweather.com/docs/api/weather/weather-now/
 * @example
 * 示例：https://xxxx.qweatherapi.com/v7/weather/now?location=101220101
 */
const fetchNowWeather = async (fetching: boolean = false) => {
  // 尝试从缓存获取天气数据
  const cachedWeatherData = localStorage.getItem(`weather_now_${cityCode.value}`)
  const nowUpdateTime = localStorage.getItem(`weather_now_time_${cityCode.value}`)

  // 检查缓存是否有效（今天的数据）
  if (!fetching && cachedWeatherData && nowUpdateTime) {
    const cachedDate = new Date(nowUpdateTime)
    const today = new Date()
    if (
      cachedDate.getDate() === today.getDate() &&
      cachedDate.getMonth() === today.getMonth() &&
      cachedDate.getFullYear() === today.getFullYear()
    ) {
      // 使用缓存数据
      nowWeatherState.value = JSON.parse(cachedWeatherData)
      return
    }
  }

  const res = await baseFetch<WeatherNowData>('/v7/weather/now', {
    location: cityCode.value,
  })
  console.log('fetchNowWeather res', res)
  nowWeatherState.value = res.now
  fxLink.value = res.fxLink
  // 缓存天气数据和更新时间
  localStorage.setItem(`weather_now_${cityCode.value}`, JSON.stringify(res.now))
  localStorage.setItem(`weather_now_time_${cityCode.value}`, res.updateTime)
}
fetchNowWeather()

/**
 * 获取最近n天的天气
 * 请求路径：/v7/weather/{days}
 * @param days 天数 3d 7d 10d 15d 30d
 * @see https://dev.qweather.com/docs/api/weather/weather-daily-forecast/
 * @example
 * 示例：https://xxxx.qweatherapi.com/v7/weather/3d?location=101220101
 */
const fetchDailyWeather = async (
  days: '3d' | '7d' | '10d' | '15d' | '30d' = '3d',
  fetching: boolean = false
) => {
  // 尝试从缓存获取每日天气数据
  const cachedDailyData = localStorage.getItem(`weather_daily_${cityCode.value}_${days}`)
  const dailyUpdateTime = localStorage.getItem(`weather_daily_time_${cityCode.value}_${days}`)

  // 检查缓存是否有效（今天的数据）
  if (!fetching && cachedDailyData && dailyUpdateTime) {
    const cachedDate = new Date(dailyUpdateTime)
    const today = new Date()
    if (
      cachedDate.getDate() === today.getDate() &&
      cachedDate.getMonth() === today.getMonth() &&
      cachedDate.getFullYear() === today.getFullYear()
    ) {
      // 使用缓存数据
      dailyWeatherState.value = JSON.parse(cachedDailyData)
      return
    }
  }

  const res = await baseFetch<WeatherDailyData>(`/v7/weather/${days}`, {
    location: cityCode.value,
  })
  console.log('fetchDailyWeather res', res)
  dailyWeatherState.value = res.daily
  fxLink.value = res.fxLink
  // 缓存每日天气数据和更新时间
  localStorage.setItem(`weather_daily_${cityCode.value}_${days}`, JSON.stringify(res.daily))
  localStorage.setItem(`weather_daily_time_${cityCode.value}_${days}`, res.updateTime)
}
fetchDailyWeather()

const fetchWeather = async (fetching: boolean = false) => {
  try {
    await fetchNowWeather(fetching)
    await fetchDailyWeather('3d', fetching)
  } catch (error) {
    console.error('刷新天气失败', error)
  }
}

const panelVisible = ref(false)
const cityState = ref<LocationItem[]>([])
const cityOptions = ref<{ label: string; value: string; city: LocationItem }[]>([])

const fetchLocation = async () => {
  if (!cityKeyword.value) return
  fetching.value = true
  const res = await baseFetch<CityData>('/geo/v2/city/lookup', {
    location: cityKeyword.value,
  })
  console.log('fetchLocation res', res)
  cityState.value = res.location
  fetching.value = false
  cityOptions.value = res.location.map((item) => ({
    label: item.name + '-' + item.adm1,
    value: item.id,
    city: item,
  }))
}

const handleFocus = () => {
  fetchLocation()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleSelect = (value: string, option: any) => {
  cityCode.value = value
  cityName.value = option.city.name
  cityKeyword.value = option.city.name
  console.log('handleSelect value', value, cityName.value)
  fetchWeather(true)
}

const fetching = ref(false)
const handleSearch = debounce((input: string) => {
  console.log('handleSearch input', input)
  cityKeyword.value = input
  fetchLocation()
}, 800)
</script>

<style scoped lang="less">
.baseWeather {
  padding: 0 0.1rem;
  cursor: pointer;
  user-select: none;
  position: relative;
  min-width: 1.6rem;
  display: flex;
  justify-content: flex-end;

  &.size-small {
    font-size: 0.14rem;

    .textMode {
      gap: 0;
    }
    .cardMode {
      gap: 0.04rem;
    }

    .city {
      font-size: 0.14rem;
    }

    .text {
      font-size: 0.14rem;
    }

    .temp {
      font-size: 0.14rem;
      font-weight: bold;
    }
  }

  &.size-medium {
    font-size: 0.16rem;

    .textMode,
    .cardMode {
      gap: 0.06rem;
    }

    .city {
      font-size: 0.16rem;
    }

    .text {
      font-size: 0.16rem;
    }

    .temp {
      font-size: 0.16rem;
      font-weight: bold;
    }
  }

  &.size-large {
    font-size: 0.24rem;

    .textMode,
    .cardMode {
      gap: 0.08rem;
    }

    .city {
      font-size: 0.24rem;
    }

    .text {
      font-size: 0.24rem;
    }

    .temp {
      font-size: 0.24rem;
      font-weight: bold;
    }
  }

  .textMode {
    display: flex;
    align-items: center;

    .city {
      font-weight: bold;
      margin-right: 0.08rem;
    }

    .icon {
      display: flex;
      align-items: center;
    }

    .text {
      margin: 0 0.08rem;
    }

    .temp {
      font-weight: bold;
    }
  }

  .cardMode {
    display: flex;
    align-items: center;

    .icon {
      display: flex;
      align-items: center;
      margin-right: 0.12rem;
    }

    .city {
      font-weight: bold;
      margin-bottom: 0.04rem;
    }

    .text {
      margin-right: 0.08rem;
    }

    .temp {
      font-weight: bold;
    }
  }

  .panel {
    position: absolute;
    top: 100%;
    left: 0;
    width: 220px;
    background: rgb(249 250 251);
    box-shadow: 0 0 0.08rem rgba(0, 0, 0, 0.5);
    border-radius: 0.08rem;
    padding: 0.08rem 0.12rem;
    color: #000;
    cursor: default;
    text-align: left;
    line-height: 1.5;
    z-index: 1999;

    .panel-search {
      font-size: 14px;
      margin-top: 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      .title {
        font-weight: bold;
      }

      .extra {
        font-size: 0.14rem;
        .close {
          cursor: pointer;
        }
        .ant-btn-link {
          padding: 0;
        }
      }
    }

    .panel-item {
      font-size: 0.14rem;
      margin-bottom: 0.04rem;
    }
  }
}
</style>
